import React from "react";

/**
 * RvDataCardSkeleton
 * Shimmer pulse placeholder card while data is loading.
 */
export function RvDataCardSkeleton() {
  return (
    <div className="rv-datacard rv-datacard--skeleton" aria-hidden="true">
      <div className="rv-datacard-header">
        <div className="rv-datacard-header-main">
          <div className="rv-skeleton-line rv-skeleton-line--title" />
          <div className="rv-skeleton-line rv-skeleton-line--sub" />
        </div>
        <div className="rv-skeleton-pill" />
      </div>
      <div className="rv-datacard-body">
        <div className="rv-datacard-grid">
          <div className="rv-datacard-cell">
            <div className="rv-skeleton-line rv-skeleton-line--label" />
            <div className="rv-skeleton-line rv-skeleton-line--val" />
          </div>
          <div className="rv-datacard-cell">
            <div className="rv-skeleton-line rv-skeleton-line--label" />
            <div className="rv-skeleton-line rv-skeleton-line--val" />
          </div>
          <div className="rv-datacard-cell">
            <div className="rv-skeleton-line rv-skeleton-line--label" />
            <div className="rv-skeleton-line rv-skeleton-line--val" />
          </div>
          <div className="rv-datacard-cell">
            <div className="rv-skeleton-line rv-skeleton-line--label" />
            <div className="rv-skeleton-line rv-skeleton-line--val" />
          </div>
        </div>
      </div>
      <div className="rv-datacard-footer">
        <div className="rv-skeleton-line rv-skeleton-line--foot" />
        <div className="rv-skeleton-actions" />
      </div>
    </div>
  );
}

/**
 * RvDataCard
 *
 * Standalone data card component for clean tabular-to-card representation.
 *
 * Props:
 *   index       - Optional row/item index (number or string, e.g. 1)
 *   title       - Primary item title/name (string or ReactNode)
 *   subtitle    - Code, SKU, or secondary identifier (string or ReactNode)
 *   badge       - Status or tag badge component (ReactNode)
 *   tag         - Optional secondary chip (ReactNode)
 *   fields      - Array of { label: string, value: any, fullWidth?: boolean }
 *   actions     - Action buttons (ReactNode, e.g. edit/delete inline buttons)
 *   onClick     - Click handler for the card body (e.g. to open detail drawer)
 *   className   - Optional extra classes
 */
export function RvDataCard({
  index,
  title,
  subtitle,
  badge,
  tag,
  fields = [],
  actions,
  onClick,
  className = "",
}) {
  const isClickable = typeof onClick === "function";

  return (
    <div
      className={`rv-datacard ${isClickable ? "rv-datacard--clickable" : ""} ${className}`.trim()}
      onClick={onClick}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
    >
      {/* ── Card Header ── */}
      <div className="rv-datacard-header">
        <div className="rv-datacard-header-main">
          {index !== undefined && index !== null && (
            <span className="rv-datacard-idx">#{index}</span>
          )}
          <div className="rv-datacard-titles">
            <div className="rv-datacard-title">{title}</div>
            {subtitle && <div className="rv-datacard-sub">{subtitle}</div>}
          </div>
        </div>

        <div className="rv-datacard-header-badges">
          {tag && <div className="rv-datacard-tag">{tag}</div>}
          {badge && <div className="rv-datacard-badge">{badge}</div>}
        </div>
      </div>

      {/* ── Card Body (Key-Value Grid) ── */}
      {fields.length > 0 && (
        <div className="rv-datacard-body">
          <div className="rv-datacard-grid">
            {fields.map((f, i) => (
              <div
                key={i}
                className={`rv-datacard-cell ${f.fullWidth ? "rv-datacard-cell--full" : ""}`}
              >
                <span className="rv-datacard-label">{f.label}</span>
                <span className="rv-datacard-val">{f.value ?? "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Card Footer ── */}
      {(actions || isClickable) && (
        <div className="rv-datacard-footer">
          <div className="rv-datacard-footer-hint">
            {isClickable && (
              <span className="rv-datacard-hint-text">
                Tap to view details <span className="rv-datacard-arrow">→</span>
              </span>
            )}
          </div>
          {actions && (
            <div
              className="rv-datacard-actions"
              onClick={(e) => e.stopPropagation()}
            >
              {actions}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * RvDataCardList
 *
 * Wrapper container managing:
 * - Loading skeletons
 * - Standardized empty state
 * - Card mapping
 *
 * Props:
 *   loading        - boolean
 *   skeletonCount  - number of skeleton cards (default 3)
 *   items          - array of items
 *   renderCard     - (item, index) => <RvDataCard ... />
 *   emptyIcon      - ReactNode icon for empty state
 *   emptyTitle     - string
 *   emptySubtitle  - string
 *   emptyAction    - ReactNode (e.g. primary button)
 *   className      - extra container className
 */
export function RvDataCardList({
  loading = false,
  skeletonCount = 3,
  items = [],
  renderCard,
  emptyIcon,
  emptyTitle = "No items found",
  emptySubtitle,
  emptyAction,
  className = "",
}) {
  if (loading) {
    return (
      <div className={`rv-datacard-list rv-datacard-list--loading ${className}`.trim()}>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <RvDataCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className={`rv-datacard-empty ${className}`.trim()}>
        {emptyIcon && <div className="rv-datacard-empty-icon">{emptyIcon}</div>}
        <h4 className="rv-datacard-empty-title">{emptyTitle}</h4>
        {emptySubtitle && (
          <p className="rv-datacard-empty-sub">{emptySubtitle}</p>
        )}
        {emptyAction && (
          <div className="rv-datacard-empty-action">{emptyAction}</div>
        )}
      </div>
    );
  }

  return (
    <div className={`rv-datacard-list ${className}`.trim()}>
      {items.map((item, index) => renderCard(item, index))}
    </div>
  );
}

export default RvDataCard;
